<script setup lang="ts">
  import CPButton from '@ui/components/CPButton.vue'
  import CPDivider from '@ui/components/CPDivider.vue'
  import CPText from '@ui/components/CPText.vue'

  import { useI18n } from 'vue-i18n'

  import { createElementId } from '@common/core-layer/utils'
  import { twMerge } from 'tailwind-merge'

  import AppSidebarContent from './AppSidebarContent.vue'
  import AppSidebarFooter from './AppSidebarFooter.vue'
  import AppSidebarHeader from './AppSidebarHeader.vue'

  const props = defineProps({
    colorScheme: {
      type: String,
      default: 'background',
      validator: (value: string) => ['background', 'secondary'].includes(value),
    },
    showToggle: { type: Boolean, default: true },
    togglePosition: { type: String, default: 'header' },
    isCollapsed: { type: Boolean, default: false },
    showHeader: { type: Boolean, default: true },
    onToggle: { type: Function, required: true },
    showBorder: { type: Boolean, default: true },
    showDivider: { type: Boolean, default: true },
    showFooter: { type: Boolean, default: undefined },
    class: { type: String, default: '' },
    asideClass: { type: String, default: '' },
  })

  const { t } = useI18n()

  const handleToggle = () => {
    if (props.onToggle) props.onToggle()
  }

  const showFooterSection = props.showFooter === true || (props.showToggle && props.togglePosition === 'footer')
</script>

<template>
  <div :class="twMerge('h-full', props.isCollapsed ? 'w-20' : 'w-full', props.class)">
    <!-- Sidebar -->
    <aside
      :class="
        twMerge([
          'relative z-30 flex h-full w-full flex-shrink-0 flex-col px-2 transition-all duration-300 ease-in-out border-r border-sidebar border-solid border-t-0 border-b-0 border-l-0',
          props.colorScheme === 'secondary' ? 'bg-secondary' : 'bg-sidebar',
          'max-md:relative',
          props.asideClass,
        ])
      "
    >
      <AppSidebarHeader v-if="props.showHeader">
        <slot name="header">
          <div class="flex items-center">
            <slot name="logo">
              <CPText size="xl" weight="semibold" class="sidebar-default-logo">
                {{ t('common.ui.logo') }}
              </CPText>
            </slot>
          </div>
        </slot>
        <CPButton
          v-if="props.showToggle && props.togglePosition === 'header'"
          :id="createElementId('sidebar', 'toggle', 'button')"
          :title="props.isCollapsed ? t('common.ui.expandSidebar') : t('common.ui.collapseSidebar')"
          size="sm"
          type="secondary"
          class="flex-shrink-0"
          @click="handleToggle"
        >
          {{ props.isCollapsed ? t('common.ui.expandArrow') : t('common.ui.collapseArrow') }}
        </CPButton>
      </AppSidebarHeader>

      <CPDivider v-if="props.showHeader && props.showDivider" margin-top="0px" margin-bottom="0px" />

      <AppSidebarContent :show-border="props.showBorder">
        <slot name="content">
          <div class="p-4 text-center opacity-60">
            <CPText size="sm" class="text-muted">
              {{ t('common.ui.addContentSlot') }}
            </CPText>
          </div>
        </slot>
      </AppSidebarContent>

      <AppSidebarFooter v-if="showFooterSection" :show-border="props.showBorder">
        <slot name="footer" />
        <CPButton
          v-if="props.showToggle && props.togglePosition === 'footer'"
          id="sidebar-toggle-footer"
          :title="props.isCollapsed ? t('common.ui.expandSidebar') : t('common.ui.collapseSidebar')"
          size="sm"
          type="secondary"
          class="flex-shrink-0"
          @click="handleToggle"
        >
          {{ props.isCollapsed ? t('common.ui.expandArrow') : t('common.ui.collapseArrow') }}
        </CPButton>
      </AppSidebarFooter>
    </aside>
  </div>
</template>
